import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";

import { CalendarEventStatus, type CalendarEvent } from "@fitness-app/data-models/entities/CalendarEvent";

import { useUserRole } from "~/hooks/trainer/useUserRole";
import { supabase } from "~/store/initializeStore";

dayjs.extend(isoWeek);

/** The signed-in team member's own consultations for the current ISO week. */
export const useScheduledEvents = (enabled = true) => {
  const { isTeamMember, userId } = useUserRole();
  const currentDate = dayjs();
  const startOfWeek = currentDate.startOf("isoWeek");
  const endOfWeek = currentDate.endOf("isoWeek").add(1, "day");

  const { data, isLoading } = useQuery(["scheduled_events", userId, startOfWeek.format("YYYY-MM-DD")], {
    enabled: enabled && isTeamMember,
    queryFn: async () => {
      const { error, data } = await supabase
        .from("calendar_event")
        .select("*, trainee:traineeId(id, firstName, lastName)")
        .eq("userId", userId)
        .gte("startTime", startOfWeek.toISOString())
        .lte("endTime", endOfWeek.toISOString())
        .eq("status", CalendarEventStatus.ACTIVE)
        .order("startTime", { ascending: true })
        .overrideTypes<CalendarEvent[]>();

      if (error) {
        return [];
      }

      return data;
    },
  });

  return {
    data,
    isLoading,
    weekRange: {
      start: startOfWeek,
      end: endOfWeek,
    },
  };
};
